import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

/* --- External API --- */
let addToastFn;
export const toast = {
    show({ type = 'info', title, message, icon, duration = 4000, position = 'right' }) {
        if (addToastFn) {
            addToastFn({ type, title, message, icon, duration, position });
        } else {
            console.warn('Toast system not ready.');
        }
    },
};

/* --- Animations --- */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;


/* --- Styled Components --- */
const getPositionStyle = (position) => {
    switch (position) {
        case 'left': return css `left: 20px; align-items: flex-start;`;
        case 'center': return css `left: 50%; transform: translateX(-50%); align-items: center;`;
        case 'right':
        default: return css `right: 20px; align-items: flex-end;`;
    }
};

const ToastWrapper = styled.div`
  position: fixed;
  top: 20px;
  z-index: 100000000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({ $position }) => getPositionStyle($position)}
`;

const ToastStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch; /* Make sure children stretch full height */
  min-width: 400px;
  max-width: 600px;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  animation: ${({ $exiting }) => $exiting ? fadeSlideOut : fadeSlideIn} 0.4s ease forwards;
  position: relative;
  border: 1px solid #DBDBDB;
  font-family: 'Poppins';
`;

const VerticalBar = styled.div`
  width: 8px;
  background-color: ${({ type }) => getBorderColor(type)};
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  display: flex;
  flex: 1;
  padding: 14px 16px;
  text-align: left;
`;

const ToastIcon = styled.div`
  font-size: 24px;
  margin-right: 12px;
  margin-top: 2px;
`;

const TextGroup = styled.div`
  flex: 1;
`;

const ToastTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const ToastMessage = styled.div`
  font-size: 1.3rem;
  color: #2d2d2d;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 8px;
  align-self: start;
  color: #999;
`;

/* --- Helpers --- */
const getBorderColor = (type) => {
    switch (type) {
        case 'success': return '#85B33E';
        case 'error': return '#C00C0F';
        case 'info': return '#F7C631';
        case 'warning': return '#FF8A00';
        default: return '#85B33E';
    }
};

let toastId = 0;

/* --- Main Component --- */
const ToastNotification = () => {
    const [toasts, setToasts] = useState([]);
    const activeTimers = useRef([]);

    const addToast = ({ type, title, message, icon, duration, position }) => {
        const id = ++toastId;
        setToasts((prev) => [...prev, { id, type, title, message, icon, position, exiting: false }]);

        const timerId = setTimeout(() => {
            removeToast(id);
            activeTimers.current = activeTimers.current.filter(tId => tId !== timerId);
        }, duration);
        activeTimers.current.push(timerId);
    };
    
    const removeToast = (id) => {
        setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
      );

      const animationTimerId = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        activeTimers.current = activeTimers.current.filter(tId => tId !== animationTimerId);
      }, 400); // match animation duration

      activeTimers.current.push(animationTimerId);
  
    };

    useEffect(() => {
      addToastFn = addToast;

      // Return a cleanup function that runs when the component unmounts
      return () => {
          // Cancel all active timers to prevent state updates
          activeTimers.current.forEach(timerId => clearTimeout(timerId));
          // Clean up the global function reference
          addToastFn = null; 
      };
    }, []); // Empty array ensures this runs only on mount and unmount

    useEffect(() => {
        addToastFn = addToast;
    }, []);

    return (
        <>
            {['left', 'center', 'right'].map((pos) => (
                <ToastWrapper key={pos} $position={pos}>
                    {toasts
                        .filter((t) => t.position === pos)
                        .map(({ id, type, title, message, icon, exiting }) => (
                            <ToastStyled key={id} type={type} $exiting={exiting}>
                                <VerticalBar type={type} />
                                <ToastContent>
                                    {icon && <ToastIcon>{icon}</ToastIcon>}
                                    <TextGroup>
                                        {title && <ToastTitle>{title}</ToastTitle>}
                                        {message && <ToastMessage>{message}</ToastMessage>}
                                    </TextGroup>
                                </ToastContent>
                                <ToastClose onClick={() => removeToast(id)}>×</ToastClose>
                            </ToastStyled>
                        ))}
                </ToastWrapper>
            ))}
        </>
    );
};

export default ToastNotification;
